import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyLine = () => ({ product_id: "", qty_ctn: "", qty_pcs: "" });

export function TransferPlanForm({ open, onOpenChange, warehouses, products, plan, onSaved }) {
  const [source, setSource] = useState("");
  const [dest, setDest] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSource(plan?.source_warehouse_id || "");
    setDest(plan?.dest_warehouse_id || "");
    setNote(plan?.note || "");
    setLines(
      plan
        ? plan.lines.map((l) => ({ product_id: l.product_id, qty_ctn: l.planned_ctn || "", qty_pcs: l.planned_pcs || "" }))
        : [emptyLine()]
    );
  }, [open, plan]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const prodFor = (id) => products.find((p) => p.id === id);

  const save = async () => {
    if (saving) return;
    if (!source) return toast.error("Select the source warehouse");
    if (!dest) return toast.error("Select the destination warehouse");
    if (source === dest) return toast.error("Destination must differ from source");
    const used = lines.filter((l) => l.product_id);
    if (!used.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const body = {
        source_warehouse_id: source,
        dest_warehouse_id: dest,
        note,
        lines: used.map((l) => ({ product_id: l.product_id, qty_ctn: Number(l.qty_ctn) || 0, qty_pcs: Number(l.qty_pcs) || 0 })),
      };
      if (plan) await API.put(`/transfer-plans/${plan.id}`, body);
      else await API.post("/transfer-plans", body);
      toast.success(plan ? "Plan updated" : "Transfer plan created");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="transfer-plan-form-dialog">
        <DialogHeader>
          <DialogTitle>{plan ? "Edit Transfer Plan" : "New Transfer Plan"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>From (source) *</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="mt-1.5" data-testid="plan-source-select">
                <SelectValue placeholder="Select source warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id} data-testid={`plan-source-option-${w.id}`}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>To (destination) *</Label>
            <Select value={dest} onValueChange={setDest}>
              <SelectTrigger className="mt-1.5" data-testid="plan-dest-select">
                <SelectValue placeholder="Office Stock or another warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.filter((w) => w.id !== source).map((w) => (
                  <SelectItem key={w.id} value={w.id} data-testid={`plan-dest-option-${w.id}`}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 space-y-3" data-testid="plan-lines-editor">
          {lines.map((l, i) => {
            const p = prodFor(l.product_id);
            const ppc = p?.pcs_per_ctn || 0;
            const total = Math.round(((Number(l.qty_ctn) || 0) * ppc + (Number(l.qty_pcs) || 0)) * 100) / 100;
            return (
              <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`plan-line-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Item {i + 1}</Label>
                    <Select value={l.product_id} onValueChange={(v) => setLine(i, { product_id: v })}>
                      <SelectTrigger className="mt-1" data-testid={`plan-line-product-${i}`}>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((pr) => (
                          <SelectItem key={pr.id} value={pr.id}>{pr.name}{pr.sku ? ` (${pr.sku})` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {lines.length > 1 && (
                    <Button variant="ghost" size="icon" className="mt-5" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} data-testid={`plan-line-remove-${i}`} aria-label="Remove line">
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </div>
                {l.product_id && (
                  <div className={`mt-2 grid gap-2 ${ppc > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {ppc > 0 && (
                      <div>
                        <Label className="text-xs">Cartons (1 ctn = {ppc} pcs)</Label>
                        <Input type="number" min="0" value={l.qty_ctn} onChange={(e) => setLine(i, { qty_ctn: e.target.value })} className="mt-1" data-testid={`plan-line-ctn-${i}`} />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">{p?.unit || "pcs"}</Label>
                      <Input type="number" min="0" value={l.qty_pcs} onChange={(e) => setLine(i, { qty_pcs: e.target.value })} className="mt-1" data-testid={`plan-line-pcs-${i}`} />
                    </div>
                  </div>
                )}
                {l.product_id && total > 0 && (
                  <p className="mt-1.5 text-[11px] font-medium text-indigo-600" data-testid={`plan-line-total-${i}`}>
                    Planned: {total} {p?.unit || "pcs"}
                  </p>
                )}
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} data-testid="plan-add-line-button">
            <Plus className="mr-1 h-4 w-4" /> Add Item
          </Button>
        </div>
        <div className="mt-4">
          <Label>Plan note (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Restock office for dispatch week" className="mt-1.5" data-testid="plan-note-input" />
        </div>
        <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid="plan-save-button">
          {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          {saving ? "Saving..." : plan ? "Save Changes" : "Create Plan"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
